import { useCallback, useEffect, useRef, useState } from 'react';

import { Header } from './components/layout/Header';
import { WorkflowStepper } from './components/layout/WorkflowStepper';
import { ProcessingOverlay } from './components/layout/ProcessingOverlay';
import { useWorkflowSteps } from './components/layout/workflowSteps';
import { selectExportAvailability } from './components/layout/exportAvailability';
import { ImportTab } from './components/inputs/ImportTab';
import { MatchingReview } from './components/inputs/MatchingReview';
import { AdjustmentWorkspace } from './components/results/AdjustmentWorkspace';
import { ExportPanel } from './components/results/ExportPanel';
import { PrintReport } from './components/results/PrintReport';
import { Alert, cn, toast } from './components/ui';

import { useAnalysis } from './hooks/useAnalysis';
import { EXAMPLES } from './data/examples';
import { useAppStore, pickPersistedState } from './store/appStore';
import { selectReportInput } from './store/selectors';
import { downloadFile, exportToPNEZD, parsePairFile } from './utils/fileParser';
import { buildAnalysisLog } from './utils/reportLog';
import {
  parseProject,
  projectFileName,
  serializeProject,
} from './utils/projectFile';
import type { PointPair } from './types';

const store = useAppStore.getState;

/** One toast at a time offers to undo the latest replacement. */
const REPLACEMENT_TOAST_ID = 'replacement';
const REPLACEMENT_TOAST_MS = 12_000;

const SYSTEM_NAMES = { 1: 'Record', 2: 'Field' } as const;

const readText = (
  file: File,
  onLoad: (content: string) => void,
  onError: () => void
) => {
  const reader = new FileReader();
  reader.onload = (event) => onLoad((event.target?.result as string) ?? '');
  reader.onerror = onError;
  reader.readAsText(file);
};

const READ_ERROR =
  'Could not read this file. Your current project is unchanged.';

function App() {
  const activeTab = useAppStore((s) => s.activeTab);
  const system1Points = useAppStore((s) => s.system1Points);
  const system2Points = useAppStore((s) => s.system2Points);
  const pairCount = useAppStore((s) => s.pointPairs.length);
  const units = useAppStore((s) => s.units);
  const steps = useWorkflowSteps();

  const analysis = useAnalysis();
  const { cancel, clearCache, operationError, setOperationError, handleSolve } =
    analysis;

  const [pairImportPreview, setPairImportPreview] = useState<{
    pairs: PointPair[];
    skipped: number;
  } | null>(null);
  useEffect(() => setPairImportPreview(null), [system1Points, system2Points]);
  const [projectError, setProjectError] = useState<string | null>(null);

  // Older projects retain their screen identifier; all analysis screens share Adjust.
  const workflowTab =
    activeTab === 'results' || activeTab === 'blunder' ? 'pairs' : activeTab;

  // --- Replacement undo -----------------------------------------------------

  /** Toast title for the next replacement, set by the handler that causes it. */
  const replacementLabel = useRef<string | null>(null);
  const replaceWith = useCallback((label: string, replace: () => void) => {
    replacementLabel.current = label;
    replace();
    replacementLabel.current = null;
  }, []);

  const undoReplacement = useCallback(() => {
    cancel();
    store().undoReplacement();
    clearCache();
    toast('Previous data restored');
  }, [cancel, clearCache]);

  // Every replacement (import, matching, example, project, reset) offers
  // Undo in a toast; the Project menu keeps it reachable afterwards.
  useEffect(
    () =>
      useAppStore.subscribe((state, previous) => {
        if (state.replacementSnapshot === previous.replacementSnapshot) return;
        if (!state.replacementSnapshot) {
          toast.dismiss(REPLACEMENT_TOAST_ID);
          return;
        }
        const label =
          replacementLabel.current ??
          (previous.pointPairs.length ? 'Pairs replaced' : 'Pairs accepted');
        toast(label, {
          id: REPLACEMENT_TOAST_ID,
          description:
            'Undo restores the previous data until the next replacement or reload.',
          duration: REPLACEMENT_TOAST_MS,
          action: { label: 'Undo', onClick: undoReplacement },
        });
      }),
    [undoReplacement]
  );

  // --- Project ----------------------------------------------------------------

  const handleOpenProject = useCallback(
    (file: File) => {
      readText(
        file,
        (content) => {
          const result = parseProject(content);
          if (!result.ok) {
            setProjectError(result.error);
            return;
          }
          setProjectError(null);
          cancel();
          replaceWith(`Opened ${file.name}`, () =>
            store().loadProject(result.data)
          );
        },
        () => setProjectError(READ_ERROR)
      );
    },
    [cancel, replaceWith]
  );

  const handleSaveProject = useCallback(() => {
    const fileName = projectFileName();
    downloadFile(
      serializeProject(pickPersistedState(store())),
      fileName,
      'application/json'
    );
    toast('Project saved', { description: fileName });
  }, []);

  const handleLoadExample = (key: string) => {
    const example = EXAMPLES.find((e) => e.key === key);
    cancel();
    replaceWith(`Loaded the ${example?.name ?? key} example`, () =>
      store().loadExample(key)
    );
  };

  const handleReset = () => {
    cancel();
    replaceWith('Project reset', () => store().reset());
    clearCache();
  };

  // Import a pair file into the matching review (Adjust).
  const handleImportPairs = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      readText(
        file,
        (content) => {
          const { system1Points: s1, system2Points: s2 } = store();
          const parsed = parsePairFile(content, s1, s2);
          if (parsed.pairs.length) {
            setProjectError(null);
            setPairImportPreview(parsed);
          } else
            setProjectError(
              `No valid pair rows could be read (${parsed.skipped} skipped). Current pairs are unchanged.`
            );
        },
        () => setProjectError(READ_ERROR)
      );
    },
    []
  );

  // --- Exports (all require current results) ---------------------------------

  const exportCsv = useCallback(() => {
    const state = store();
    if (selectExportAvailability(state).csv) return;
    downloadFile(
      exportToPNEZD(
        state.transformedPoints.map((p) => ({
          point: p.newPoint,
          north: p.n2,
          east: p.e2,
          description: p.description,
        }))
      ),
      'transformed-points.csv'
    );
  }, []);

  const exportLog = useCallback(() => {
    if (selectExportAvailability(store()).report) return;
    downloadFile(
      buildAnalysisLog(selectReportInput(store())),
      'resolve-analysis.log'
    );
  }, []);

  const printReport = useCallback(() => {
    if (selectExportAvailability(store()).report) return;
    // Let the menu close and return focus before the print dialog opens.
    window.setTimeout(() => window.print(), 0);
  }, []);

  const errors = [
    operationError && {
      key: 'operation',
      message: operationError,
      dismiss: () => setOperationError(null),
      label: 'Dismiss calculation error',
    },
    projectError && {
      key: 'project',
      message: projectError,
      dismiss: () => setProjectError(null),
      label: 'Dismiss',
    },
  ].filter((error) => !!error);

  const contentWidth =
    workflowTab === 'pairs' ? 'max-w-screen-2xl' : 'max-w-6xl';

  return (
    <div className="min-h-screen bg-canvas text-fg print:min-h-0 print:bg-surface">
      {/* Print-only report */}
      <PrintReport />

      <div className="flex min-h-screen flex-col print:hidden">
        <ProcessingOverlay onCancel={cancel} />
        <Header
          examples={EXAMPLES}
          onLoadExample={handleLoadExample}
          onOpenProject={handleOpenProject}
          onSaveProject={handleSaveProject}
          onUndoReplacement={undoReplacement}
          onReset={handleReset}
          onExportCsv={exportCsv}
          onExportLog={exportLog}
          onPrintReport={printReport}
          onCancelProcessing={cancel}
        />
        <WorkflowStepper
          steps={steps}
          activeStep={workflowTab}
          onStepChange={(step) => store().setActiveTab(step)}
        />

        {(errors.length > 0 || pairImportPreview) && (
          <div
            className={cn(
              'mx-auto w-full space-y-3 px-4 pt-4 sm:px-6 sm:pt-6',
              contentWidth
            )}
          >
            {errors.map((error) => (
              <Alert
                key={error.key}
                tone="danger"
                onDismiss={error.dismiss}
                dismissLabel={error.label}
              >
                <span className="text-fg">{error.message}</span>
              </Alert>
            ))}
            {pairImportPreview && (
              <MatchingReview
                pairs={pairImportPreview.pairs}
                skippedRows={pairImportPreview.skipped}
                system1Points={system1Points}
                system2Points={system2Points}
                replacedCount={pairCount}
                onAccept={(reviewedPairs) => {
                  replaceWith('Pairs replaced', () =>
                    store().setPointPairs(reviewedPairs)
                  );
                  setPairImportPreview(null);
                }}
                onCancel={() => setPairImportPreview(null)}
              />
            )}
          </div>
        )}

        <main
          role="tabpanel"
          id={`panel-${workflowTab}`}
          aria-labelledby={`tab-${workflowTab}`}
          tabIndex={0}
          className="flex-1 outline-offset-[-2px]"
        >
          {workflowTab === 'import' && (
            <ImportTab
              system1Points={system1Points}
              system2Points={system2Points}
              onPointsLoaded={(points, systemNumber) =>
                replaceWith(
                  `${points.length} ${SYSTEM_NAMES[systemNumber]} points imported`,
                  () => store().loadSystemPoints(points, systemNumber)
                )
              }
              onClearPoints={(systemNumber) =>
                replaceWith(
                  `${SYSTEM_NAMES[systemNumber]} points cleared`,
                  () => store().clearSystemPoints(systemNumber)
                )
              }
              units={units}
              onUnitsChange={(value) => store().setUnits(value)}
              onContinue={() => store().setActiveTab('pairs')}
              examples={EXAMPLES}
              onLoadExample={handleLoadExample}
            />
          )}

          {workflowTab === 'pairs' && (
            <div className="mx-auto w-full max-w-screen-2xl px-4 py-4 sm:px-6 sm:py-6">
              <AdjustmentWorkspace
                analysis={analysis}
                onImportPairs={handleImportPairs}
              />
            </div>
          )}

          {workflowTab === 'transform' && (
            <ExportPanel
              onResolve={() => handleSolve()}
              onDownloadCsv={exportCsv}
              onGoToAdjust={() => store().setActiveTab('pairs')}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
